import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import axios from 'axios';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function ContactModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        interest: '',
        message: '',
      });
      // Close modal after 2 seconds on success
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      logError('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[520px] bg-surface border border-line-1 text-stark p-0 overflow-hidden rounded-md"
        data-testid="contact-modal"
      >
        {/* Subtle signal accent strip */}
        <div className="absolute top-0 left-0 w-full h-px bg-signal" />
        
        <div className="relative p-6 md:p-8">
          <DialogHeader className="mb-6">
            <p className="eyebrow section-eyebrow-line mb-3">Get In Touch</p>
            <DialogTitle className="font-heading text-2xl md:text-3xl font-bold text-stark tracking-tight">
              Let's Build Together
            </DialogTitle>
            <p className="text-stark-2 mt-2">
              Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </DialogHeader>

          <AnimatePresence mode="wait">
            {submitStatus === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ ease: [0.16, 1, 0.3, 1] }}
                className="py-12 text-center"
                data-testid="contact-success-message"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-available/10 border border-available/30 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-available" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-stark mb-2">
                  Message Sent
                </h3>
                <p className="text-stark-2">
                  We'll be in touch soon.
                </p>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onSubmit={handleSubmit}
                className="space-y-5"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-stark-2 text-xs uppercase tracking-wider font-mono">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-stark-2 text-xs uppercase tracking-wider font-mono">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="you@company.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-stark-2 text-xs uppercase tracking-wider font-mono">Company</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company"
                    data-testid="contact-company-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interest" className="text-stark-2 text-xs uppercase tracking-wider font-mono">I'm interested in...</Label>
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    className="w-full h-10 px-3.5 rounded-md bg-ink-2 border border-line-1 text-stark font-body text-sm focus:border-white focus:outline-none focus:ring-0 hover:border-line-2 transition-colors"
                    data-testid="contact-interest-select"
                  >
                    <option value="" className="bg-surface">Select an option</option>
                    <option value="ai-automation" className="bg-surface">AI Automation Solutions</option>
                    <option value="prompt-engineering" className="bg-surface">Prompt Engineering & Strategy</option>
                    <option value="digital-transformation" className="bg-surface">Digital Transformation</option>
                    <option value="ai-education" className="bg-surface">AI Education & Training</option>
                    <option value="consultation" className="bg-surface">General Consultation</option>
                    <option value="partnership" className="bg-surface">Partnership Opportunity</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-stark-2 text-xs uppercase tracking-wider font-mono">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project or how we can help..."
                    rows={4}
                    className="bg-ink-2 border-line-1 text-stark placeholder:text-stark-3 placeholder:font-mono placeholder:text-sm focus:border-white focus-visible:ring-0 hover:border-line-2 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 rounded-md bg-signal/[0.08] border border-signal/30 text-signal"
                    data-testid="contact-error-message"
                  >
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">Something went wrong. Please try again.</span>
                  </motion.div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-white hover:bg-stark-2/95 text-black font-semibold h-12 rounded-md"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ContactModal;
